import React from 'react';
import Localize from 'react-intl-universal';

import { format } from 'date-fns';
import { enUS, de } from 'date-fns/locale';

import ChairAltOutlinedIcon from '@mui/icons-material/ChairAltOutlined';
import ContentPasteOffIcon from '@mui/icons-material/ContentPasteOff';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Paper from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import {
  formatDateTimeStringToDateObject,
  formatLongDateTimeStringToDateTimeObject,
  formatPrice,
  seatsInfo
} from '@common/Util.jsx';
import AudioPreview from '@components/AudioPreview/AudioPreview.jsx';
import RichTextEditor, { readStyledText } from '@components/RichTextEditor/RichTextEditor';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
// eslint-disable-next-line import/order

import ColumnWithPopup from './ColumnWithPopup.jsx';
import { getSessionDuration } from './SessionsHelper.jsx';
import SessionSpeakers from './SessionSpeakers.jsx';

export const CustomDivider = () => (
  <Divider
    sx={{
      borderColor: 'divider',
      borderBottomWidth: 1,
      borderRadius: 1,
      width: '98%',
      margin: '1rem auto'
    }}
  />
);

const ChipStyled = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontSize: '0.9rem',
  fontWeight: 'bold',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem'
  }
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[1000],
  marginRight: 0,
  lineHeight: '1.9',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    marginRight: '1rem'
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[1000],
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  marginRight: '1rem',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'start',
    width: 'auto'
  }
}));

const StyledDetailsBox = styled(Box)(({ theme }) => ({
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '0 3%',
  justifyContent: 'center',
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'start'
  }
}));

const StyledIcon = styled(Icon)(() => ({
  fontSize: '1.2rem',
  alignSelf: 'center',
  marginRight: '5px'
}));

const SessionCard = ({ session = {} }) => {
  const descriptionString = readStyledText(session, 'description');
  const isEmptyDescription = !descriptionString;
  const theme = useTheme();
  const [contextData] = useGlobalContext();

  const locationNames = [];
  const meetingRoomNames = [];

  session?.meetingRoomsDtos?.forEach((room) => {
    const temp = `${room?.venue?.city} ${room?.venue?.city && ', '}${room?.venue?.country}`;
    if (!locationNames.includes(temp)) {
      locationNames.push(temp);
    }
    if (!meetingRoomNames.includes(room?.name)) {
      meetingRoomNames.push(room?.name);
    }
  });

  let browserLanguage = navigator.language || (navigator.languages && navigator.languages[0]);

  const sessionPrice = formatPrice(session?.price);
  const sessionCurrency = session?.standardPrice?.currency?.value || '';

  if (!browserLanguage) {
    browserLanguage = 'en-US';
  }
  const locale = browserLanguage.toLowerCase().startsWith('de') ? de : enUS;
  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${session?.id}-content`}
        id={`panel${session?.id}-header`}
        sx={{ m: 0 }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            alignItems: 'center',
            flex: 1,
            p: 1
          }}
        >
          <ChipStyled
            icon={<ScheduleIcon color={theme?.palette?.common?.white} />}
            label={format(session.startDateTime, 'HH:mm', { locale })}
          />
          <Typography
            sx={{
              mb: 1,
              display: 'inline-block',
              marginLeft: '1rem',
              fontSize: { xs: '1.1rem', sm: '1.2rem' }
            }}
          >
            {`${session.name}${session.type?.value ? ` (${session.type?.value})` : ''}`}
          </Typography>
        </Paper>
      </AccordionSummary>
      <AccordionDetails>
        <StyledDetailsBox display='flex'>
          <StyledTypography>
            <EventAvailableIcon />
            {`${Localize.get('EventDetails.start')}: `}
            {formatLongDateTimeStringToDateTimeObject(session.startDateTime)}
          </StyledTypography>
          <StyledTypography>
            <EventBusyIcon />
            {`${Localize.get('EventDetails.end')}: `}
            {formatLongDateTimeStringToDateTimeObject(session.endDateTime)}
          </StyledTypography>
          <StyledTypography>
            <ScheduleIcon />
            {Localize.get('EventDetails.duration')} {getSessionDuration(session)}
          </StyledTypography>
          {!!locationNames.length && (
            <StyledBox>
              <StyledIcon>location_on</StyledIcon>
              {locationNames?.length > 1 ? (
                <ColumnWithPopup
                  contentArray={locationNames}
                  modalHeaderLabel={Localize.get('App.location')}
                />
              ) : (
                locationNames[0]
              )}
            </StyledBox>
          )}
          {!!meetingRoomNames?.length && (
            <StyledBox>
              <StyledIcon>flag</StyledIcon>
              {meetingRoomNames.length > 1 ? (
                <ColumnWithPopup
                  contentArray={meetingRoomNames}
                  modalHeaderLabel={Localize.get('App.location')}
                />
              ) : (
                meetingRoomNames[0]
              )}
            </StyledBox>
          )}

          <StyledBox>
            <StyledIcon>sell_icon</StyledIcon>
            <StyledTypography>
              {sessionPrice
                ? `${sessionPrice} ${sessionCurrency} `
                : Localize.get('CustomerLoyaltyEvents.free')}
            </StyledTypography>
          </StyledBox>

          <StyledBox sx={{ alignItems: 'center' }}>
            <ChairAltOutlinedIcon />
            <StyledTypography>
              {seatsInfo(session?.availableSeats, contextData?.isSeatsNumber)}
            </StyledTypography>
          </StyledBox>

          {session.eventHasWaitingList && (
            <StyledBox sx={{ alignItems: 'center' }}>
              <StyledIcon>hourglass_empty</StyledIcon>
              <StyledTypography>{Localize.get('MyAreaIltSessionsTab.available')}</StyledTypography>
            </StyledBox>
          )}
          {session.eventRegistrationDeadline && (
            <StyledBox>
              <StyledIcon>event_available</StyledIcon>
              <StyledTypography>
                {formatDateTimeStringToDateObject(session.eventRegistrationDeadline)}
              </StyledTypography>
            </StyledBox>
          )}
          {session.eventCancellationDeadline && (
            <StyledBox>
              <StyledIcon>event_busy</StyledIcon>
              <StyledTypography>
                {formatDateTimeStringToDateObject(session.eventCancellationDeadline)}
              </StyledTypography>
            </StyledBox>
          )}
        </StyledDetailsBox>
        <CustomDivider />
        {!!session?.audioDescription?.url && (
          <Box sx={{ margin: '15px auto', width: 'fit-content' }}>
            <AudioPreview
              name={session?.audioDescription?.name}
              url={session?.audioDescription?.url}
            />
          </Box>
        )}
        <Box sx={{ mt: 1, padding: '0 3%', mb: 3 }}>
          {!isEmptyDescription ? (
            <RichTextEditor
              initialValue={session?.description}
              disabled
              isToolbarHidden
              customStyles={{ minHeight: '30px', maxHeight: '200px', overflow: 'auto' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ContentPasteOffIcon fontSize='large' sx={{ mr: 1 }} />
              {Localize.get('CourseDetails.noDesc')}
            </Box>
          )}
        </Box>
        {!!session?.teamMembers?.length && <CustomDivider />}
        <Box
          data-testid={'sessionSpeakerContainer'}
          sx={{ mb: 1, padding: '0.4rem', display: 'flex', flexWrap: 'wrap' }}
        >
          <Box sx={{ display: { xs: 'inherit', md: 'flex', padding: '0 3%' }, flexWrap: 'wrap' }}>
            {!!session?.teamMembers?.length &&
              session?.teamMembers?.map((speakers, index) => (
                <SessionSpeakers key={index} speakers={speakers} />
              ))}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default SessionCard;
